import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	DetailLayoutComponent,
	IBackEvent,
	ITagStats,
	StatsComponent,
} from '@tecalisdev/infinity';
import { LoadingService } from '../../../shared/services/loading.service';
import { MenuItemAction } from '../../../shared/models/master.models';
import { IIdentityBlockPayload } from '../../../identities/models/identities.models';
import { IdentitiesService } from '../../../identities/services/identities.service';
import { ModalBlockIdentityComponent } from '../../../shared/components/modal-block-identity/modal-block-identity.component';
import { ModalChangeStatusComponent } from '../../../shared/components/modal-change-status/modal-change-status.component';
import { ModalUnlockIdentityComponent } from '../../../shared/components/modal-unlock-identity/modal-unlock-identity.component';
import { OperationDetailCardsComponent } from '../../components/operation-detail-cards/operation-detail-cards.component';
import { OperationDetailItemComponent } from '../../components/operation-detail-item/operation-detail-item.component';
import { OperationDetailJsonComponent } from '../../components/operation-detail-json/operation-detail-json.component';
import { OperationDetailMediaPreviewComponent } from '../../components/operation-detail-media-preview/operation-detail-media-preview.component';
import { OperationDetailTabsComponent } from '../../components/operation-detail-tabs/operation-detail-tabs.component';
import { IOperationEvaluationDetail } from '../../../processes/models/processes.models';
import {
	IOperationDetailResponse,
	IOperationTfaDetailBody,
	IOperationTfaMessagingDetailData,
	isTfaOperationDetail,
	OPERATION_TYPE,
} from '../../models/operations.models';
import { OperationsService } from '../../services/operations.service';
import { OperationsHelperService } from '../../services/operations-helper.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { DetailResourceErrorComponent } from '../../../shared/components/detail-resource-error/detail-resource-error.component';
import {
	DetailResourceErrorView,
	resolveDetailResourceError,
} from '../../../core/utils/http-resource-error.utils';
import { notAvailableLabel } from '../../../core/utils/utils';


@Component({
	selector: 'app-operations-detail',
	imports: [
		AlertComponent,
		DetailLayoutComponent,
		TranslatePipe,
		StatsComponent,
		OperationDetailItemComponent,
		OperationDetailTabsComponent,
		OperationDetailMediaPreviewComponent,
		OperationDetailCardsComponent,
		OperationDetailJsonComponent,
		ModalChangeStatusComponent,
		ModalBlockIdentityComponent,
		ModalUnlockIdentityComponent,
		DetailResourceErrorComponent,
	],
	templateUrl: './operations-detail.component.html',
	styleUrl: './operations-detail.component.scss',
})
export class OperationsDetailComponent implements OnInit {
	private activatedRoute = inject(ActivatedRoute);
	private loading = inject(LoadingService);
	private operationsService = inject(OperationsService);
	private operationsHelper = inject(OperationsHelperService);
	private navigationService = inject(NavigationService);
	private identitiesService = inject(IdentitiesService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	public detail: IOperationDetailResponse;
	public id: string;
	public error: HttpErrorResponse | null = null;
	public resourceError: DetailResourceErrorView | null = null;
	public errorActionMsg: string = '';
	public showModalChangeStatus: boolean = false;
	public showModalBlockIdentity: boolean = false;
	public showModalUnlockIdentity: boolean = false;

	ngOnInit(): void {
		this.id = this.activatedRoute.snapshot.params['id'];
		this.getDetail();

		// Misma lógica que detalle de proceso: regenerar actividad al cambiar idioma (títulos, estados)
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			if (this.detail) {
				this.operationsHelper.setActivityLogs(this.detail);
			}
		});
	}

	/** UUID de identidad para modales (misma convención que detalle de proceso con `identity.uuid`). */
	public get identityUuidForModals(): string {
		return this.detail?.identity?.uuid || '';
	}

	/** Opción documento en modales: operación OCR con identificador disponible. */
	public get showOcrBlockOption(): boolean {
		return this.detail?.operation_type_name === OPERATION_TYPE.OCR && !!this.detail?.id_client;
	}

	/** Opción rostro en modales: operación POL. */
	public get showFaceBlockOption(): boolean {
		return this.detail?.operation_type_name === OPERATION_TYPE.POL;
	}

	/** Columna derecha: capturas OCR/POL/DOCUMENTS o documentos de evaluación financiera (`document[].url`). */
	public hasAsideMediaPreview(): boolean {
		if (!this.detail?.data) return false;
		const images = (this.detail.data as { images?: Record<string, unknown> }).images;
		if (images) {
			const preview = images['ocr_preview'] as { front?: string | null; back?: string | null } | undefined;
			const hasPreviewPair = !!(
				(typeof preview?.front === 'string' && preview.front.trim() !== '') ||
				(typeof preview?.back === 'string' && preview.back.trim() !== '')
			);
			const hasArrayMedia = [
				'selfie',
				'ocr_front',
				'ocr_back',
				'ocr_front_clear',
				'ocr_back_clear',
				'temporary',
				'document',
			].some((key) => {
				const group = images[key];
				return Array.isArray(group) && group.some((url) => typeof url === 'string' && url.trim() !== '');
			});
			if (hasPreviewPair || hasArrayMedia) {
				return true;
			}
		}
		return this.operationsHelper.getFinancialEvaluationDocumentUrls(this.detail).length > 0;
	}

	/** Bloque lateral de scoring (mismo criterio que la pestaña Detalles / collapse Datos). */
	public showAsideFinancialScoring(): boolean {
		if (!this.detail?.data) return false;
		const op = this.detail.operation_type_name;
		if (
			op !== OPERATION_TYPE.CONNECTIONS &&
			op !== OPERATION_TYPE.FINANCIAL_EVALUATION &&
			op !== OPERATION_TYPE.EVALUATION
		) {
			return false;
		}
		const ev = this.detail.data as unknown as IOperationEvaluationDetail;
		return ev?.scoring !== undefined && ev?.scoring !== null;
	}

	public financialEvaluationScoringTitle(): string {
		const ev = this.detail?.data as unknown as IOperationEvaluationDetail | undefined;
		if (ev?.scoring === undefined || ev?.scoring === null) return notAvailableLabel(this.trans);
		return String(ev.scoring);
	}

	/** Duración lateral (Figma): `data.total_time` o `detail.total_time` en segundos. */
	public showAsideFinancialDuration(): boolean {
		if (!this.detail?.data) return false;
		if (!this.isFinancialEvaluationOperationType()) return false;
		return this.asideDurationTotalSeconds() !== null;
	}

	public asideDurationValueFormatted(): string {
		const sec = this.asideDurationTotalSeconds();
		if (sec === null) return notAvailableLabel(this.trans);
		return this.formatAsideDurationSeconds(sec);
	}

	public asideDurationAverageDescription(): string {
		const avg = this.detail?.average_operation_time;
		if (avg === undefined || avg === null || Number.isNaN(Number(avg))) {
			return '';
		}
		const line = this.formatAsideDurationSeconds(Number(avg));
		return this.trans.instant('OPERATIONS.DETAIL.AVG_DURATION_LINE', { line });
	}

	public asideDurationTagRounded(): ITagStats | null {
		const total = this.asideDurationTotalSeconds();
		const avg = this.detail?.average_operation_time;
		if (total === null || avg === undefined || avg === null || Number(avg) === 0) {
			return null;
		}
		const avgN = Number(avg);
		const diffPercent = ((total - avgN) / avgN) * 100;
		if (diffPercent < 0) {
			return { icon: 'icon-trending-up', label: Math.abs(diffPercent).toFixed(0) + '%' };
		}
		if (diffPercent > 0) {
			return { icon: 'icon-trending-down', label: Math.abs(diffPercent).toFixed(0) + '%', styling: 'red' };
		}
		return null;
	}

	private isFinancialEvaluationOperationType(): boolean {
		const op = this.detail?.operation_type_name;
		return (
			op === OPERATION_TYPE.CONNECTIONS ||
			op === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			op === OPERATION_TYPE.EVALUATION
		);
	}

	private asideDurationTotalSeconds(): number | null {
		if (!this.detail?.data) return null;
		const ev = this.detail.data as unknown as IOperationEvaluationDetail;
		const fromEval = ev?.total_time;
		if (fromEval !== undefined && fromEval !== null && !Number.isNaN(Number(fromEval))) {
			return Number(fromEval);
		}
		if (this.detail.total_time !== undefined && this.detail.total_time !== null) {
			return Number(this.detail.total_time);
		}
		return null;
	}

	private formatAsideDurationSeconds(seconds: number): string {
		const m = Math.floor(seconds / 60);
		const s = Math.round(seconds % 60);
		return `${m}m ${s}s`;
	}

	public onHeaderMenuAction(action: MenuItemAction): void {
		const actions: Partial<Record<MenuItemAction, () => void>> = {
			[MenuItemAction.STATUS]: () => {
				this.showModalChangeStatus = true;
			},
			[MenuItemAction.BLOCK]: () => {
				this.showModalBlockIdentity = true;
			},
			[MenuItemAction.UNLOCK]: () => {
				this.showModalUnlockIdentity = true;
			},
		};
		actions[action]?.();
	}

	public closeChangeStatus(reload: boolean): void {
		if (reload) this.getDetail();
		this.showModalChangeStatus = false;
	}

	public closeBlockIdentity(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalBlockIdentity = false;
		if (payload && typeof payload === 'object' && this.detail?.process_uuid) {
			this.blockIdentity(payload as Partial<IIdentityBlockPayload>);
		}
	}

	public closeUnlockIdentity(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalUnlockIdentity = false;
		if (payload && typeof payload === 'object' && this.detail?.process_uuid) {
			this.blockIdentity(payload as Partial<IIdentityBlockPayload>);
		}
	}

	private blockIdentity(payload: Partial<IIdentityBlockPayload>): void {
		this.loading.show(this.trans.instant('LOADING.BLOCKING_IDENTITY'));
		this.identitiesService.blockIdentity(this.getBlockIdentityBody(payload)).subscribe({
			next: () => {
				this.getDetail();
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.errorActionMsg = error?.error?.message || this.trans.instant('ERRORS.GENERIC_DESCRIPTION');
			},
			complete: () => {},
		});
	}

	private getBlockIdentityBody(payload: Partial<IIdentityBlockPayload>): IIdentityBlockPayload {
		if (payload.blocked_ip === true && this.detail?.ip) {
			payload.ip = [this.detail.ip];
		}

		if ((payload.blocked_identifier === true || payload.blocked_identifier === false) && this.detail?.id_client) {
			payload.identifier = this.detail.id_client;
		}

		if (payload.blocked_face === true) {
			payload.operation_uuid = this.detail.uuid;
		}

		return {
			...payload,
			process_uuid: this.detail.process_uuid,
			company_uuid: this.detail.company_uuid,
		};
	}

	public getDetail(): void {
		this.error = null;
		this.resourceError = null;
		this.loading.show(this.trans.instant('LOADING.PROCESS_DETAIL'));

		if (this.isTfaDetailRoute()) {
			this.loadTfaDetail();
			return;
		}

		this.operationsService.detail(this.id).subscribe({
			next: (response) => {
				this.applyDetail(response);
				this.loading.hide();
			},
			error: (error: HttpErrorResponse) => {
				// Red de seguridad: deep link sin `?entity=tfa` (p. ej. uuid TFA pegado a mano).
				if (error.status === 404) {
					this.loadTfaDetail();
					return;
				}
				this.error = error;
				this.resourceError = resolveDetailResourceError(error);
				this.detail = undefined;
				this.loading.hide();
			},
		});
	}

	/** Listado mensajería navega con `?entity=tfa` para no golpear `/operation/{uuid}`. */
	private isTfaDetailRoute(): boolean {
		return this.activatedRoute.snapshot.queryParamMap.get('entity') === 'tfa';
	}

	private loadTfaDetail(): void {
		this.operationsService.tfaDetail(this.id).subscribe({
			next: (tfa) => this.applyDetail(this.normalizeTfaDetail(this.id, tfa)),
			error: (tfaError: HttpErrorResponse) => {
				this.error = tfaError;
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private applyDetail(response: IOperationDetailResponse): void {
		if (response?.data && typeof response.data === 'string') {
			try {
				response.data = JSON.parse(JSON.parse(response.data));
			} catch (e) {
				console.error('Error al parsear el JSON:', e);
			}
		}

		this.detail = response;
		this.operationsHelper.setActivityLogs(this.detail);
	}

	private normalizeTfaDetail(id: string, tfa: IOperationTfaDetailBody): IOperationDetailResponse {
		const data: IOperationTfaMessagingDetailData & Record<string, unknown> = {
			phone: tfa.phone,
			channel: tfa.channel,
			success: tfa.success,
			attempts: tfa.attempts,
			communications: tfa.communications,
			sibling_tfa_sessions: tfa.sibling_tfa_sessions,
		};

		const createdAt = tfa.created_at;
		const updatedAt = tfa.updated_at;
		let totalTime = tfa.total_time;
		if (totalTime == null && createdAt && updatedAt) {
			const diffMs = Date.parse(updatedAt) - Date.parse(createdAt);
			if (!Number.isNaN(diffMs) && diffMs >= 0) {
				totalTime = Math.round(diffMs / 1000);
			}
		}

		return {
			uuid: String(tfa.uuid ?? id),
			entity: 'tfa',
			process_uuid: tfa.process_uuid,
			company: tfa.company,
			company_uuid: tfa.company_uuid,
			name: tfa.name,
			nationality: tfa.nationality ?? undefined,
			id_client: tfa.id_client ?? tfa.identity?.identifier ?? undefined,
			identity: tfa.identity ?? undefined,
			state_uuid: tfa.process_state_uuid,
			state_name: tfa.process_state_name ?? tfa.state_name,
			operation_type_name: tfa.operation_type_name ?? OPERATION_TYPE.TFA,
			data,
			total_time: totalTime,
			total_tries: tfa.total_tries ?? tfa.attempts,
			ip: tfa.ip ?? undefined,
			start_at: tfa.start_at ?? createdAt ?? undefined,
			end_at: tfa.end_at ?? updatedAt ?? undefined,
			created_at: createdAt,
			updated_at: updatedAt,
			activity: [],
		};
	}

	public goBack({ event }: IBackEvent): void {
		if (isTfaOperationDetail(this.detail)) {
			this.navigationService.navigateTo({ routeSegments: ['operations', 'messaging'] }, event);
			return;
		}

		if (this.detail?.operation_type_uuid) {
			this.navigationService.navigateTo(
				{ routeSegments: ['operations', this.detail.operation_type_uuid] },
				event,
			);
			return;
		}

		this.navigationService.navigateTo({ routeSegments: ['operations'] }, event);
	}
}
